import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { RadioButtons } from 'components/Radio/RadioButtons';
import { H2 } from 'components/Text/H2';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { CmsApiPersonaQuestion } from 'modules/cms/api/types/blocks/CmsApiPersonaQuizBlock';
import { colors } from 'theme';
import { imageUrl } from 'utils/filestack';
import { retinaBackgroundImage } from 'utils/style';

const TitleImage = styled(Box)<{
  $handle: string;
  $mimetype: string | undefined;
}>`
  background-size: cover;
  background-position: center;
  ${({ $handle: handle, $mimetype: mimetype }) =>
    retinaBackgroundImage(
      imageUrl({
        data: { handle, mimetype },
        width: 900,
        height: 500,
        fit: 'clip',
        insecureSVG: false,
      }),
      {
        filename2x: imageUrl({
          data: { handle, mimetype },
          width: 900,
          height: 500,
          fit: 'clip',
          insecureSVG: false,
        }),
        include3x: false,
      },
    )};
`;

type Props = {
  blockIndex?: number;
  question: CmsApiPersonaQuestion;
  index: number;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateScore?: (...args: Array<any>) => any;
  answered?: boolean;
};

export function CmsPersonaQuizBlockQuestion({
  blockIndex,
  question: { title, image, answers },
  index,
  updateScore,
  answered,
}: Props) {
  const options = useMemo(
    () =>
      answers.map(({ title: label }, value) => ({
        label,
        value,
      })),
    [answers],
  );
  const [value, setValue] = useState(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChange = (newValue: any) => {
    const newIndex = parseInt(newValue, 10);
    // @ts-expect-error TS(2345): Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    setValue(newIndex);

    if (updateScore) {
      updateScore(index, answers[newIndex].points);
    }
  };

  return (
    <Box
      data-qa-id={`cms-persona-quiz-question-${index}`}
      // @ts-expect-error TS(2322): Type 'false | (number | null)[]' is not assignable... Remove this comment to see the full error message
      mt={index > 0 && [32, null, 72]}
      opacity={answered ? 0.7 : 1}
    >
      <HeadingSmall mb={18}>
        <Box
          display="inline-block"
          bg={colors.inputGrey}
          color={colors.lightContentGrey}
          borderRadius="3px"
          px="8px"
        >
          {`${getText('Question')} ${index + 1}`}
        </Box>
      </HeadingSmall>
      <Box
        position="relative"
        width={1}
        overflow="hidden"
        borderRadius={16}
        textAlign="center"
      >
        {image && (
          <TitleImage
            data-qa-id="question-image"
            $handle={image.handle}
            $mimetype={image.mimetype}
            position="absolute"
            top={0}
            left={0}
            width={1}
            height="100%"
          />
        )}
        <Box
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width={1}
          minHeight={['56.25vw', null, null, 500]}
          p={[24, null, null, 64]}
          bg="rgba(0,0,0,0.4)"
          color="white"
        >
          <H2
            analyticsData={
              blockIndex
                ? {
                    eventName: 'CMS Block',
                    eventProperties: {
                      block_position: blockIndex,
                      block_type: 'Persona Quiz',
                    },
                  }
                : {}
            }
            data-qa-id="question-title"
            // @ts-expect-error TODO fix me
            styleAs={title.length > 80 ? 'h2' : 'h1'}
            my={0}
            dangerouslySetInnerHTML={{
              // @ts-expect-error TODO fix me
              __html: title,
            }}
          />
        </Box>
      </Box>
      <Box mt={36} textAlign="left">
        <RadioButtons
          large
          hideRadioButtons
          buttons={options}
          value={value}
          data-qa-id="question-answers"
          onChange={onChange}
          columns={2}
          disabled={answered}
        />
      </Box>
    </Box>
  );
}
